/**
 * DiagnocatCtToPlanPanel — КТ → Diagnocat → план (Staff LK).
 * Каталог может приходить из родительского wizard (hideCatalog + externalStudies).
 */
import { useEffect, useMemo, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import { staffApi } from '../../../../api/staff'
import type { ConstructorPlan } from '../constructorTypes'
import { WorkflowStepProgress } from './WorkflowStepProgress'
import {
  buildDiagnocatCtToPlanSteps,
  type DiagnocatPlanComposePhase,
  type DiagnocatPresentationPhase,
} from './domain/diagnocatWorkflow'
import { useDiagnocatCtSubmitWorkflow } from './useDiagnocatCtSubmitWorkflow'
import type { VatechCtCatalogStudy } from './domain/vatechCtCatalog'
import { pickDefaultVatechStudy } from './domain/vatechCtCatalog'
import type { DiagnocatStudyRef } from './domain/enrichVatechStudiesWithDiagnocat'
import { enrichVatechStudiesWithDiagnocat } from './domain/enrichVatechStudiesWithDiagnocat'
import { usePatientVatechCtCatalog } from './usePatientVatechCtCatalog'
import { VatechCtCatalogTable } from './VatechCtCatalogTable'
import { ctArchivePcSuffix } from './domain/ctArchiveLabel'
import { PlanDiagnosticsReviewPanel } from './PlanDiagnosticsReviewPanel'
import { PlanGammaPresentationStep } from './PlanGammaPresentationStep'
import { useConstructorWorkflow } from '../shell/ConstructorWorkflowContext'
import { useConstructorDiagnosticsDraft } from '../shell/ConstructorDiagnosticsDraftContext'
import {
  PLAN_DIAGNOSTICS_INITIAL_PROMPT,
  PLAN_DIAGNOSTICS_MODEL_CHOICE,
  isRestorableDiagnosticsProposal,
  recoverLatestDiagnosticsProposal,
} from './planDiagnosticsCompose'
import { prefetchPresentationPhotoSources } from './planPresentation'
import {
  WORKFLOW_CHECKPOINT_KEYS,
  formatCheckpointSavedAt,
  savePlanWorkflowCheckpoint,
  clearPlanWorkflowCheckpoint,
  type PlanWorkflowCheckpoint,
} from '../workflowCheckpoint'
import { usePatientNasPhotoPrefetch } from './usePatientNasPhotoPrefetch'

const ACTION_KEY = 'plan_diagnostics_auto_compose'
const UI_SURFACE = 'staff_lk_plan_diagnostics_beta'

function extractMisPlanId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as Record<string, unknown>
  const apply = p.apply_result ?? p.result ?? p
  if (!apply || typeof apply !== 'object') return null
  const a = apply as Record<string, unknown>
  if (typeof a.target_entity_id === 'string') return a.target_entity_id
  const created = a.created_plan
  if (created && typeof created === 'object' && typeof (created as { plan_id?: string }).plan_id === 'string') {
    return (created as { plan_id: string }).plan_id
  }
  return null
}

export function DiagnocatCtToPlanPanel({
  bootstrap,
  session,
  plan,
  lkPatientId,
  misPatientId,
  staffUserId,
  diagnosticsLinked,
  externalStudies,
  externalDiagnocatData = null,
  selectedStudyUid: externalSelectedUid,
  onSelectStudy,
  hideCatalog = false,
  onApplied,
  onMisPlansRefresh,
  onAnalysisReady,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  plan?: ConstructorPlan | null
  lkPatientId: number | null
  misPatientId: string | null
  staffUserId: string
  diagnosticsLinked: boolean
  externalStudies?: VatechCtCatalogStudy[]
  externalDiagnocatData?: {
    total_analyses: number
    studies?: Array<{ study_uid: string; study_date?: string | null; analyses?: unknown[] }>
  } | null
  selectedStudyUid?: string
  onSelectStudy?: (studyUid: string) => void
  hideCatalog?: boolean
  onApplied?: () => void
  onMisPlansRefresh?: (misPlanId: string | null) => void
  onAnalysisReady?: () => void
}) {
  const [internalSelectedUid, setInternalSelectedUid] = useState('')
  const { openWindow } = useConstructorWorkflow()
  const diagnosticsDraft = useConstructorDiagnosticsDraft()
  const [composePhase, setComposePhase] = useState<DiagnocatPlanComposePhase>('idle')
  const [composeError, setComposeError] = useState<string | null>(null)
  const [proposal, setProposal] = useState<{ proposal_id: string; proposed_payload?: Record<string, unknown> | null; override_payload?: Record<string, unknown> | null } | null>(null)
  const [applyMessage, setApplyMessage] = useState('')
  const [appliedMisPlanId, setAppliedMisPlanId] = useState<string | null>(null)
  const [presentationPhase, setPresentationPhase] = useState<DiagnocatPresentationPhase>('idle')
  const [restoredFromCheckpoint, setRestoredFromCheckpoint] = useState(false)
  const [draftRestoredHint, setDraftRestoredHint] = useState<string | null>(null)

  const selectedStudyUid = externalSelectedUid ?? internalSelectedUid
  const setSelectedStudyUid = onSelectStudy ?? setInternalSelectedUid

  const tenantId = bootstrap.tenant
  const internalCatalog = usePatientVatechCtCatalog({
    bootstrap,
    session,
    lkPatientId,
    misPatientId,
    enabled: externalStudies === undefined && !hideCatalog && Boolean(lkPatientId),
  })

  const studies = useMemo(() => {
    const base = externalStudies ?? internalCatalog.studies
    return enrichVatechStudiesWithDiagnocat(base, externalDiagnocatData?.studies as DiagnocatStudyRef[] | undefined)
  }, [externalStudies, internalCatalog.studies, externalDiagnocatData?.studies])
  const catalogLoading = externalStudies === undefined && !hideCatalog && internalCatalog.isLoading
  const catalogError = internalCatalog.isError ? String(internalCatalog.error) : ''

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${session.accessToken}` }), [session.accessToken])
  const apiBase = bootstrap.apiBase

  const checkpointKey = WORKFLOW_CHECKPOINT_KEYS.planDiagnosticsBeta
  const savedCheckpoint = plan?.workflow_checkpoints?.[checkpointKey] ?? null

  useEffect(() => {
    if (!plan?.id || restoredFromCheckpoint || proposal || !misPatientId) return

    const restoreProposal = (raw: unknown, hint: string) => {
      if (!isRestorableDiagnosticsProposal(raw)) return false
      setProposal(raw as NonNullable<typeof proposal>)
      setComposePhase('preview')
      setComposeError(null)
      setDraftRestoredHint(hint)
      setRestoredFromCheckpoint(true)
      return true
    }

    const cp = savedCheckpoint
    if (cp?.proposal && (cp.step === 'proposal_ready' || cp.step === 'compose_failed')) {
      if (restoreProposal(cp.proposal, `Сохранённый черновик от ${formatCheckpointSavedAt(cp) ?? 'ранее'}.`)) return
    }

    let cancelled = false
    void recoverLatestDiagnosticsProposal({ apiBase, authHeader, misPatientId }).then((recovered) => {
      if (cancelled || !recovered) return
      if (restoreProposal(recovered, 'Найден готовый черновик в MIS — можно проверить и применить.')) return
    })
    return () => {
      cancelled = true
    }
  }, [plan?.id, savedCheckpoint, restoredFromCheckpoint, proposal, misPatientId, apiBase, authHeader])


  const workflow = useDiagnocatCtSubmitWorkflow({
    bootstrap,
    session,
    misPatientId,
    lkPatientId,
    enabled: Boolean(misPatientId) && Boolean(lkPatientId),
    diagnocatSeed: externalDiagnocatData,
    onAnalysisReady,
  })

  const nasPrefetch = usePatientNasPhotoPrefetch({
    bootstrap,
    session,
    patientId: misPatientId ?? '',
    enabled: workflow.analysisReady && Boolean(misPatientId),
  })

  useEffect(() => {
    if (!appliedMisPlanId || !misPatientId) {
      setPresentationPhase('idle')
      return
    }
    setPresentationPhase('active')
    void prefetchPresentationPhotoSources({
      bootstrap,
      session,
      patientId: misPatientId,
      planId: appliedMisPlanId,
    })
  }, [appliedMisPlanId, bootstrap, misPatientId, session])

  useEffect(() => {
    if (selectedStudyUid || studies.length === 0) return
    const picked = pickDefaultVatechStudy(studies)
    if (picked?.study_uid) setSelectedStudyUid(picked.study_uid)
  }, [selectedStudyUid, studies, setSelectedStudyUid])

  const selectedStudy = useMemo(
    () => studies.find((s) => s.study_uid === selectedStudyUid) ?? null,
    [studies, selectedStudyUid],
  )

  const snapshotAnalyses = externalDiagnocatData?.total_analyses ?? 0
  const selectedStudyAnalysisReady = Boolean(
    selectedStudy?.diagnocat_analysis_ready || (selectedStudy?.diagnocat_analyses_count ?? 0) > 0,
  )
  const effectiveAnalysisReady =
    workflow.analysisReady || snapshotAnalyses > 0 || selectedStudyAnalysisReady

  const stepsSnapshot = useMemo(
    () => ({
      findingsCount: effectiveAnalysisReady
        ? Math.max(
            workflow.snapshot.findingsCount,
            snapshotAnalyses,
            selectedStudy?.diagnocat_analyses_count ?? 0,
            1,
          )
        : workflow.snapshot.findingsCount,
      analysesCount: effectiveAnalysisReady
        ? Math.max(
            workflow.snapshot.analysesCount,
            snapshotAnalyses,
            selectedStudy?.diagnocat_analyses_count ?? 0,
            1,
          )
        : workflow.snapshot.analysesCount,
    }),
    [
      effectiveAnalysisReady,
      workflow.snapshot,
      snapshotAnalyses,
      selectedStudy?.diagnocat_analyses_count,
    ],
  )

  const steps = useMemo(
    () =>
      buildDiagnocatCtToPlanSteps({
        job: workflow.job,
        jobPipelineActive: workflow.jobPipelineActive,
        snapshot: stepsSnapshot,
        composePhase,
        composeError,
        nasPrefetchPhase: nasPrefetch.phase,
        nasPhotoCount: nasPrefetch.photoCount,
        presentationPhase,
      }),
    [
      workflow.job,
      workflow.jobPipelineActive,
      stepsSnapshot,
      composePhase,
      composeError,
      nasPrefetch.phase,
      nasPrefetch.photoCount,
      presentationPhase,
    ],
  )

  const canSubmitCt =
    diagnosticsLinked &&
    Boolean(misPatientId) &&
    Boolean(lkPatientId) &&
    Boolean(selectedStudyUid) &&
    selectedStudy?.submit_eligible === true &&
    !selectedStudy?.already_in_diagnocat &&
    !workflow.isSubmitting &&
    !workflow.jobPipelineActive

  const resetDraftState = () => {
    setComposePhase('idle')
    setProposal(null)
    setAppliedMisPlanId(null)
    setApplyMessage('')
    setDraftRestoredHint(null)
    setComposeError(null)
    setRestoredFromCheckpoint(true)
  }

  const resetForAnotherPlan = () => {
    resetDraftState()
  }


  useEffect(() => {
    diagnosticsDraft.setPlan(plan ?? null)
    diagnosticsDraft.setProposal(proposal)
    diagnosticsDraft.setMisPatientId(misPatientId)
    diagnosticsDraft.setStaffUserId(staffUserId)
    diagnosticsDraft.setBootstrap(bootstrap)
    diagnosticsDraft.setSession(session)
    diagnosticsDraft.setOnApplied(() => onApplied ?? null)
    diagnosticsDraft.setOnMisPlansRefresh(() => onMisPlansRefresh ?? null)
    diagnosticsDraft.setApplyHandler(() => async (data: unknown) => {
      const misPlanId = extractMisPlanId(data)
      if (misPlanId && plan?.id && !plan.mis_plan_id) {
        await fetch(`${apiBase}/staff/constructor/${plan.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify({ mis_plan_id: misPlanId }),
        })
      }
      if (misPlanId && plan?.id) {
        const appliedProposal = proposal ? { ...proposal, applied_mis_plan_id: misPlanId } : null
        await savePlanWorkflowCheckpoint(apiBase, session, plan.id, WORKFLOW_CHECKPOINT_KEYS.planDiagnosticsBeta, {
          ...(savedCheckpoint ?? { step: "applied" }),
          step: "applied",
          proposal: appliedProposal,
          applied_mis_plan_id: misPlanId,
        })
        setProposal((current) => current ? { ...current, applied_mis_plan_id: misPlanId } : current)
        diagnosticsDraft.setProposal(appliedProposal)
      }
      setAppliedMisPlanId(misPlanId)
      setComposePhase('done')
      setApplyMessage(
        misPlanId ? `План MIS создан: ${misPlanId.slice(0, 8)}…` : 'План из диагностики применён',
      )
      onMisPlansRefresh?.(misPlanId)
      onApplied?.()
      openWindow('plan-presentation')
    })
    // diagnosticsDraft is intentionally excluded: its setters are stable React
    // dispatchers, but the memoized context value changes identity every time we
    // call them here. Keeping it as a dependency made this effect re-run forever
    // and froze the whole constructor tab (main-thread render loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiBase,
    authHeader,
    bootstrap,
    misPatientId,
    onApplied,
    onMisPlansRefresh,
    openWindow,
    plan,
    proposal,
    savedCheckpoint,
    session,
    staffUserId,
  ])

  const staffAuthorReady = Boolean(staffUserId) && staffUserId !== "undefined"

  const canComposePlan =
    effectiveAnalysisReady &&
    diagnosticsLinked &&
    Boolean(misPatientId) &&
    Boolean(staffUserId) &&
    composePhase !== 'proposing' &&
    composePhase !== 'applying'

  const canRecomposeFresh =
    Boolean(misPatientId) &&
    Boolean(staffUserId) &&
    diagnosticsLinked &&
    effectiveAnalysisReady &&
    composePhase !== 'proposing' &&
    composePhase !== 'applying'

  const compose = async () => {
    if (!canRecomposeFresh || !misPatientId) return
    setComposePhase('proposing')
    setComposeError(null)
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action_key: ACTION_KEY,
          ui_surface: UI_SURFACE,
          source_kind: 'text',
          input_text: PLAN_DIAGNOSTICS_INITIAL_PROMPT,
          patient_id: misPatientId,
          target_entity_id: plan?.mis_plan_id ?? null,
          metadata: {
            consumer: 'staff_lk_ai',
            authoring_clinician_id: staffUserId,
            workspace_target: 'constructor',
            output_mode: 'staged',
            model_choice: PLAN_DIAGNOSTICS_MODEL_CHOICE,
            vatech_study_uid: selectedStudyUid || null,
            vatech_study_date: selectedStudy?.study_date ?? null,
          },
        }),
      })
      if (!res.ok) throw new Error(`proposal HTTP ${res.status}`)
      const data = (await res.json()) as { proposal: NonNullable<typeof proposal> }
      setProposal(data.proposal)
      setAppliedMisPlanId(null)
      setPresentationPhase('idle')
      setComposePhase('preview')
      openWindow('plan-review')
      if (plan?.id) {
        void savePlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey, {
          step: 'proposal_ready',
          proposal: data.proposal as Record<string, unknown>,
          last_error: null,
        })
      }
    } catch (error) {
      const err = String(error)
      try {
        const recovered = await recoverLatestDiagnosticsProposal({ apiBase, authHeader, misPatientId })
        if (recovered) {
          setProposal(recovered)
          setAppliedMisPlanId(null)
          setPresentationPhase('idle')
          setComposePhase('preview')
          setComposeError('Compose ответил ошибкой, но черновик уже готов в MIS — показан последний результат.')
          if (plan?.id) {
            void savePlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey, {
              step: 'proposal_ready',
              proposal: recovered as Record<string, unknown>,
              last_error: null,
              recovered_after_timeout: true,
            })
          }
          return
        }
      } catch {
        /* recovery optional */
      }
      setComposePhase('error')
      setComposeError(err)
    }
  }
  const discardDraftAndRecompose = async () => {
    if (plan?.id) {
      await clearPlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey)
    }
    resetDraftState()
    setRestoredFromCheckpoint(true)
    await compose()
  }


  if (!misPatientId || !lkPatientId) {
    return <p className="text-xs text-slate-500">Пациент не связан с MIS — отправка КТ недоступна.</p>
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-800">КТ → Diagnocat → план лечения</span>
        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-800">
          Diagnocat
        </span>
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
          Бета
        </span>
      </div>

      {!hideCatalog ? (
        catalogLoading ? (
          <p className="text-xs text-slate-500">Загружаем каталог КТ с {ctArchivePcSuffix(tenantId)}…</p>
        ) : catalogError ? (
          <p className="text-xs text-red-600">Каталог КТ: {catalogError}</p>
        ) : studies.length === 0 ? (
          <p className="text-xs text-slate-500">КТ не найдено на {ctArchivePcSuffix(tenantId)}</p>
        ) : (
          <VatechCtCatalogTable
            studies={studies}
            selectedStudyUid={selectedStudyUid}
            onSelectStudy={setSelectedStudyUid}
            disabled={workflow.jobPipelineActive}
          />
        )
      ) : null}


      {snapshotAnalyses > 0 && !effectiveAnalysisReady ? (
        <p className="text-xs text-amber-800">В Diagnocat есть готовый анализ ({snapshotAnalyses}) — обновите страницу или нажмите «Обновить» в связке.</p>
      ) : null}
      {snapshotAnalyses > 0 ? (
        <p className="text-xs text-emerald-800">
          Готовый анализ Diagnocat на Young — повторная отправка КТ не нужна. Выберите КТ с датой совпадающей со снимком в Diagnocat и нажмите «Составить план лечения».
        </p>
      ) : null}

      {!diagnosticsLinked ? (
        <p className="text-xs text-slate-500">Отправка КТ заблокирована до подтверждения связки Diagnocat.</p>
      ) : null}

      {selectedStudy?.already_in_diagnocat ? (
        <p className="text-xs text-sky-800">Это КТ уже загружено в Diagnocat — повторная отправка не нужна, можно сразу «Составить план лечения».</p>
      ) : null}

      {!workflow.jobPipelineActive ? (
        <button
          type="button"
          className="rounded-md bg-violet-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          disabled={!canSubmitCt}
          onClick={() => void workflow.submit(selectedStudyUid)}
        >
          {workflow.isSubmitting ? 'Отправляем…' : 'Отправить на анализ Diagnocat'}
        </button>
      ) : null}

      {workflow.job?.status === 'failed' ? (
        <button
          type="button"
          className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700"
          onClick={workflow.resetJob}
        >
          Повторить отправку
        </button>
      ) : null}

      {workflow.pollTimedOut ? (
        <p className="text-xs text-amber-700">
          Превышено время ожидания анализа. Проверьте snapshot или повторите позже.
        </p>
      ) : null}

      {selectedStudy ? (
        <p className="text-xs text-slate-600">
          Выбрано КТ: {selectedStudy.study_date ?? 'без даты'} · {selectedStudy.folder_name ?? selectedStudy.study_uid.slice(0, 12)}
          {selectedStudy.already_in_diagnocat ? ' · уже в Diagnocat' : ''}
        </p>
      ) : null}

      <WorkflowStepProgress steps={steps} ariaLabel="Прогресс Diagnocat и составления плана" />

      {hideCatalog ? (
        <p className="text-xs text-slate-600">
          КТ для плана выбирается в таблице выше. Можно выбрать другое исследование и составить отдельный план.
        </p>
      ) : null}

      <div className="space-y-2 border-t border-slate-200 pt-3">
        <button
          type="button"
          className="rounded-md bg-violet-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          disabled={!canRecomposeFresh}
          onClick={() => void compose()}
        >
          {composePhase === 'proposing' ? 'Составляем план…' : 'Составить план лечения'}
        </button>
        {!staffAuthorReady ? (
          <p className="text-xs text-slate-500">Не выбран врач-автор — войдите как врач или выберите автора плана.</p>
        ) : !effectiveAnalysisReady ? (
          <p className="text-xs text-slate-500">
            Кнопка активируется после готовности находок Diagnocat. Выберите КТ с меткой «анализ готов» (сверка по дате снимка) или отправьте новое КТ на анализ.
          </p>
        ) : composePhase === "preview" && proposal ? (
          <p className="text-xs text-slate-500">Есть сохранённый черновик — можно применить его или пересобрать ниже.</p>
        ) : null}
      </div>


      {draftRestoredHint || (composePhase === 'preview' && proposal) ? (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950">
          <p>
            <strong>Правая панель</strong> — сохранённый черновик конструктора #{plan?.id ?? '—'}, не блок «Планы МИС» слева.
            {draftRestoredHint ? ` ${draftRestoredHint}` : ''}
            {' '}
            Старый черновик (до обновления matcher) сам не обновится.
          </p>
          <p className="text-amber-900">
            После пересборки: названия и цены из прайса DHC, без потери кости в позициях, эндо → пульпит/перелечивание.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50"
              disabled={!canRecomposeFresh || composePhase === ('proposing' as DiagnocatPlanComposePhase)}
              onClick={() => void discardDraftAndRecompose()}
            >
              {composePhase === 'proposing' ? 'Пересобираем…' : 'Пересобрать beta-план (новый matcher)'}
            </button>
            <button
              type="button"
              className="rounded-md border border-violet-300 bg-white px-3 py-1.5 text-xs text-violet-900 hover:bg-violet-50"
              onClick={() => {
                resetDraftState()
                if (plan?.id) {
                  void clearPlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey)
                }
              }}
            >
              Скрыть черновик
            </button>
          </div>
          <p className="text-[10px] text-amber-800">
            Либо слева у ИИ-плана → <strong>Корректировка</strong> → «Подставить запрос matcher» → «Уточнить и пересобрать».
          </p>
        </div>
      ) : null}

      {composePhase === 'preview' && proposal ? (
        <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-950">
          <p className="font-medium">Черновик плана готов</p>
          <p className="mt-1 text-violet-900">Проверка этапов, голосовая корректировка и утверждение — в окне «Черновик / этапы» (не закрывайте — пайплайн продолжается).</p>
          <button
            type="button"
            className="mt-2 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700"
            onClick={() => openWindow('plan-review')}
          >
            Открыть проверку черновика
          </button>
        </div>
      ) : null}

      {composePhase === 'done' ? (
        <>
          <p className="text-xs text-emerald-700">{applyMessage}</p>
          <p className="text-xs text-slate-600">
            План появится в блоке «Планы МИС пациента» слева с меткой <strong>ИИ</strong>. Нажмите «Обновить», если список не обновился.
          </p>
          <button
            type="button"
            className="rounded-md border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs text-violet-900 hover:bg-violet-100"
            onClick={resetForAnotherPlan}
          >
            Составить ещё один план (другое КТ)
          </button>
          {appliedMisPlanId && misPatientId ? (
            <PlanGammaPresentationStep
              bootstrap={bootstrap}
              session={session}
              misPatientId={misPatientId}
              planId={appliedMisPlanId}
              planDetail={plan ?? null}
              onPresentationPhaseChange={setPresentationPhase}
            />
          ) : null}
        </>
      ) : null}
      {composePhase === 'error' && composeError ? (
        <p className="text-xs text-red-600">{composeError}</p>
      ) : null}
    </div>
  )
}
